import React, { useState } from 'react';
import GradientButton from './GradientButton';

const GradientButtonDemo: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const simulateLoading = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setFormSubmitted(true);
      setTimeout(() => setFormSubmitted(false), 2000);
    }, 3000);
  };

  return (
    <div style={{ padding: '32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
          GradientButton Component Demo
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Enhanced gradient buttons with multiple variants, sizes, and interactive states
        </p>
      </div>

      {/* Aquaculture Action Buttons */}
      <div style={{ marginBottom: '48px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Aquaculture Management Actions
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          padding: '24px',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <GradientButton
            variant="primary"
            icon="biomas"
            onClick={() => console.log('Add Fish clicked')}
          >
            Add Fish Stock
          </GradientButton>

          <GradientButton
            variant="info"
            icon="water"
            onClick={() => console.log('Monitor Water clicked')}
          >
            Monitor Water
          </GradientButton>

          <GradientButton
            variant="warning"
            icon="feed"
            onClick={() => console.log('Schedule Feed clicked')}
          >
            Schedule Feeding
          </GradientButton>

          <GradientButton
            variant="success"
            icon="dashboard-chart"
            onClick={() => console.log('View Reports clicked')}
          >
            View Reports
          </GradientButton>

          <GradientButton
            variant="secondary"
            icon="settings"
            onClick={() => console.log('System Settings clicked')}
          >
            System Settings
          </GradientButton>

          <GradientButton
            variant="danger"
            icon="bell"
            onClick={() => console.log('Emergency Alert clicked')}
          >
            Emergency Alert
          </GradientButton>
        </div>
      </div>

      {/* Button Variants */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Color Variants
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <GradientButton variant="primary">Primary</GradientButton>
          <GradientButton variant="secondary">Secondary</GradientButton>
          <GradientButton variant="success">Success</GradientButton>
          <GradientButton variant="warning">Warning</GradientButton>
          <GradientButton variant="danger">Danger</GradientButton>
          <GradientButton variant="info">Info</GradientButton>
        </div>
      </div>

      {/* Size Variants */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Size Variants
        </h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <GradientButton size="sm" variant="primary">Small</GradientButton>
          <GradientButton size="md" variant="primary">Medium</GradientButton>
          <GradientButton size="lg" variant="primary">Large</GradientButton>
          <GradientButton size="xl" variant="primary">Extra Large</GradientButton>
        </div>
      </div>

      {/* With Icons */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Buttons with Icons
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <GradientButton variant="primary" icon="home" iconPosition="left">
            Go Home
          </GradientButton>
          <GradientButton variant="success" icon="tasks" iconPosition="left">
            Complete Task
          </GradientButton>
          <GradientButton variant="info" icon="user" iconPosition="right">
            User Profile
          </GradientButton>
          <GradientButton variant="warning" icon="database" iconPosition="left">
            Backup Data
          </GradientButton>
        </div>
      </div>

      {/* Interactive States */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Interactive States
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <GradientButton 
            variant="primary" 
            icon="biomas"
            loading={isLoading}
            onClick={simulateLoading}
          >
            {isLoading ? 'Processing...' : 'Submit Form'}
          </GradientButton>

          <GradientButton variant="secondary" disabled>
            Disabled Button
          </GradientButton>

          {formSubmitted && (
            <GradientButton variant="success" icon="tasks">
              ✓ Submitted!
            </GradientButton>
          )}
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
          Click "Submit Form" to see loading state simulation
        </p>
      </div>

      {/* Full Width */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Full Width Buttons
        </h3>
        <div style={{ display: 'grid', gap: '12px', maxWidth: '400px' }}>
          <GradientButton variant="primary" fullWidth icon="home">
            Create New Facility
          </GradientButton>
          <GradientButton variant="success" fullWidth icon="tasks">
            Save Changes
          </GradientButton>
          <GradientButton variant="danger" fullWidth icon="bell">
            Delete Account
          </GradientButton>
        </div>
      </div>

      {/* Custom Gradient */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Custom Gradients
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <GradientButton 
            customGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            icon="dashboard-chart"
          >
            Purple Gradient
          </GradientButton>
          
          <GradientButton 
            customGradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            icon="chat"
          >
            Pink Gradient
          </GradientButton>
          
          <GradientButton 
            customGradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            icon="water"
          >
            Ocean Gradient
          </GradientButton>
          
          <GradientButton 
            customGradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
            icon="biomas"
          >
            Aqua Gradient
          </GradientButton>
        </div>
      </div>

      {/* Facility Management CTA Section */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Facility Management Call-to-Action
        </h3>
        <div style={{ 
          padding: '32px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '16px',
          border: '1px solid #bae6fd',
          textAlign: 'center'
        }}>
          <h4 style={{ fontSize: '20px', fontWeight: '600', color: '#0c4a6e', marginBottom: '8px' }}>
            Ready to optimize your aquaculture operation?
          </h4>
          <p style={{ color: '#075985', marginBottom: '24px', fontSize: '16px' }}>
            Start monitoring your fish stock, water quality, and feeding schedules today.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <GradientButton 
              variant="primary" 
              size="lg"
              icon="home"
              onClick={() => console.log('Start Free Trial clicked')}
            >
              Start Free Trial
            </GradientButton>
            <GradientButton 
              variant="secondary" 
              size="lg"
              icon="chat"
              onClick={() => console.log('Contact Sales clicked')}
            >
              Contact Sales
            </GradientButton>
          </div>
        </div>
      </div>

      {/* Button Combinations */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
          Form Button Groups
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <GradientButton variant="primary" icon="tasks">Save</GradientButton>
          <GradientButton variant="secondary">Cancel</GradientButton>
          <GradientButton variant="danger" icon="database">Delete</GradientButton>
        </div>
      </div>
    </div>
  );
};

export default GradientButtonDemo;